'use client';

import { useCallback } from 'react';

import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

import { useQueryClient } from '@tanstack/react-query';

import { useMonitoring } from '@kit/monitoring/hooks';
import { useAppEvents } from '@kit/shared/events';
import { useAuthChangeListener } from '@kit/supabase/hooks/use-auth-change-listener';

/**
 * List of private path prefixes that require authentication.
 * This extends the default list to include app-specific routes.
 */
const PRIVATE_PATH_PREFIXES = [
  '/dashboard',
  '/admin',
  '/join',
  '/identities',
  '/update-password',
];
// '/chat' must NOT be listed: guest chat is public by design (free messages,
// then the signup wall). With it listed, Supabase's INITIAL_SESSION event
// (fires ~2s after load with user=null for guests) hard-navigated every
// DIRECT guest load of /chat/<slug> — ad deep links, SEO visits, refreshes —
// back to the homepage. SPA visits from / were unaffected, which is why the
// guest funnel partly worked and this hid for weeks. ('/create-character'
// was a dead entry — the real routes are /create and /creator.)

export function AuthProvider(props: React.PropsWithChildren) {
  const dispatchEvent = useDispatchAppEventFromAuthEvent();
  const queryClient = useQueryClient();

  const onEvent = useCallback(
    (event: AuthChangeEvent, session: Session | null) => {
      dispatchEvent(event, session?.user.id, {
        email: session?.user.email ?? '',
      });

      // Invalidate caches on sign-in (handles OAuth redirect where
      // handleSignInSuccess() is never called since it's a full page navigation)
      if (event === 'SIGNED_IN' && session) {
        queryClient.invalidateQueries({ queryKey: ['supabase:user'] });
        queryClient.invalidateQueries({ queryKey: ['wallet:balance'] });
      }

      // Clear caches on sign-out so navbar updates immediately on mobile
      // (before window.location.reload() which can be slow/cached on mobile)
      if (event === 'SIGNED_OUT') {
        queryClient.setQueryData(['supabase:user'], undefined);
        queryClient.removeQueries({ queryKey: ['wallet:balance'] });
      }
    },
    [dispatchEvent, queryClient],
  );

  useAuthChangeListener({
    onEvent,
    privatePathPrefixes: PRIVATE_PATH_PREFIXES,
  });

  return props.children;
}

function useDispatchAppEventFromAuthEvent() {
  const { emit } = useAppEvents();
  const monitoring = useMonitoring();

  return useCallback(
    (
      type: AuthChangeEvent,
      userId: string | undefined,
      traits: Record<string, string> = {},
    ) => {
      switch (type) {
        case 'INITIAL_SESSION':
          if (userId) {
            emit({
              type: 'user.signedIn',
              payload: { userId, ...traits },
            });

            monitoring.identifyUser({ id: userId, ...traits });
          }

          break;

        case 'SIGNED_IN':
          if (userId) {
            emit({
              type: 'user.signedIn',
              payload: { userId, ...traits },
            });

            monitoring.identifyUser({ id: userId, ...traits });
          }

          break;

        case 'USER_UPDATED':
          emit({
            type: 'user.updated',
            payload: { userId: userId!, ...traits },
          });

          break;
      }
    },
    [emit, monitoring],
  );
}
